import React, {useEffect, useState, useCallback} from 'react';
import {XMarkIcon} from '@heroicons/react/24/outline';
import {IdentificationIcon} from '@heroicons/react/24/solid'
import styles from './BusinessLicensePopUp.module.css';
import Button from "../../../components/common/button/Button";
import {getLicenseFileAPI} from "../../../services/admin";
import {clientLicenseAPI} from "../../../services/client";

interface BusinessLicensePopUpProps {
    isOpen: boolean;
    onClose: () => void;
    clientId?: number | null;
}

//사업자등록증 조회 팝업
const BusinessLicensePopUp: React.FC<BusinessLicensePopUpProps> = ({
                                                                       isOpen,
                                                                       onClose,
                                                                       clientId = null
                                                                   }) => {
    const [loading, setLoading] = useState(false);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [fileExtension, setFileExtension] = useState<string>();
    const [error, setError] = useState<string | null>(null);

    // URL 정리 함수
    const cleanupUrl = useCallback(() => {
        if (fileUrl) {
            URL.revokeObjectURL(fileUrl);
        }
    }, [fileUrl]);

    // 상태 초기화 함수
    const resetState = useCallback(() => {
        setFileUrl(null);
        setFileExtension(undefined);
        setError(null);
        setLoading(false);
    }, []);

    // 팝업 닫기 핸들러
    const handleClose = useCallback(() => {
        cleanupUrl();
        resetState();
        onClose();
    }, [cleanupUrl, resetState, onClose]);

    useEffect(() => {
        // 팝업이 닫혔을 때는 아무것도 하지 않음
        if (!isOpen) {
            return;
        }

        const fetchLicenseFile = async () => {
            try {
                setLoading(true);
                setError(null);

                // clientId 조건에 따른 API 호출
                const result = clientId
                    ? await getLicenseFileAPI(clientId)  // clientId가 있으면 getLicenseFileAPI
                    : await clientLicenseAPI();          // clientId가 null이면 clientLicenseAPI

                const {data, extension} = result;

                // 이전 URL이 있다면 정리
                if (fileUrl) {
                    URL.revokeObjectURL(fileUrl);
                }

                // 파일 타입 확인 및 로깅
                console.log('File type:', data.type);
                console.log('File extension:', extension);
                console.log('File size:', data.size);

                // PDF 파일인 경우 MIME 타입 강제 설정
                let blob = data;
                if (extension === 'pdf' && data.type !== 'application/pdf') {
                    blob = new Blob([data], { type: 'application/pdf' });
                }

                //파일 내부 URL 생성
                const url = URL.createObjectURL(blob);
                setFileUrl(url);
                setFileExtension(extension);
            } catch (error) {
                console.error('Error fetching license:', error);
                setError('사업자등록증을 불러오는데 실패했습니다.');
            } finally {
                setLoading(false);
            }
        };

        fetchLicenseFile();

        // cleanup function - 컴포넌트가 언마운트되거나 의존성이 변경될 때 실행
        return () => {
            if (fileUrl) {
                URL.revokeObjectURL(fileUrl);
            }
        };
    }, [isOpen, clientId]); // fileUrl을 의존성에서 제거

    // 컴포넌트 언마운트 시 URL 정리
    useEffect(() => {
        return () => {
            cleanupUrl();
        };
    }, [cleanupUrl]);

    //다운로드
    const handleDownload = useCallback(() => {
        if (!fileUrl) return;

        const link = document.createElement('a');
        link.href = fileUrl;
        // clientId가 있으면 해당 ID로, 없으면 일반적인 이름으로 다운로드
        link.download = clientId
            ? `business_license_${clientId}.${fileExtension || 'pdf'}`
            : `business_license.${fileExtension || 'pdf'}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [fileUrl, clientId, fileExtension]);

    // 재시도 핸들러
    const handleRetry = useCallback(() => {
        // 현재 상태를 초기화하고 다시 로드
        cleanupUrl();
        setError(null);
        setFileUrl(null);
        setFileExtension(undefined);

        // 강제로 useEffect를 다시 실행하기 위해 로딩 상태를 변경
        setLoading(true);

        // 약간의 딜레이 후 실제 재시도 로직 실행
        setTimeout(() => {
            setLoading(false);
            // isOpen이 true인 상태에서 clientId가 변경되지 않았다면
            // useEffect가 다시 실행되지 않으므로 수동으로 트리거
            window.location.reload();
        }, 100);
    }, [cleanupUrl]);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <div className={styles.titleContainer}>
                        <IdentificationIcon className={styles.icon}/>
                        <h2 className={styles.title}>사업자등록증</h2>
                    </div>
                    {/*팝업 닫기 버튼*/}
                    <button onClick={handleClose} className={styles.closeButton}>
                        <XMarkIcon className={styles.closeIcon}/>
                    </button>
                </div>

                <div className={styles.content}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className={styles.spinner}></div>
                            <p>사업자등록증을 불러오는 중...</p>
                        </div>
                    ) : error ? (
                        // 에러 상태
                        <div className={styles.error}>
                            <p>{error}</p>
                            <Button
                                variant="secondary"
                                onClick={handleRetry}
                            >
                                다시 시도
                            </Button>
                        </div>
                    ) : fileUrl ? (
                        // 파일 조회 성공한 경우
                        <div className={styles.imageContainer}>
                            {fileExtension === 'pdf' ? (
                                // PDF 파일인 경우
                                <iframe
                                    src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&page=1&view=FitH`}
                                    className={styles.pdfViewer}
                                    title="Business License PDF"
                                    onLoad={() => console.log('PDF loaded successfully')}
                                    onError={() => {
                                        console.error('PDF iframe load error');
                                        setError('PDF를 표시할 수 없습니다. 다운로드해서 확인해주세요.');
                                    }}
                                />
                            ) : (
                                // 이미지 파일인 경우
                                <img
                                    src={fileUrl}
                                    alt="Business License"
                                    className={styles.image}
                                    onError={() => setError('이미지를 표시할 수 없습니다.')}
                                />
                            )}
                        </div>
                    ) : (
                        // 초기 상태 또는 데이터 없음
                        <div className={styles.noData}>
                        사업자등록증 데이터가 없습니다.
                        </div>
                    )}
                </div>

                <div className={styles.footer}>
                    {/*다운로드 버튼*/}
                    <Button
                        variant="primary"
                        onClick={handleDownload}
                        disabled={!fileUrl || loading}
                    >
                        다운로드
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                    >
                        닫기
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default BusinessLicensePopUp;